import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

const PLATFORMS = [
  { id: 'swiggy_online', name: 'Swiggy Online', color: '#fc8019' },
  { id: 'swiggy_dineout', name: 'Swiggy Dineout', color: '#fc8019' },
  { id: 'zomato_online', name: 'Zomato Online', color: '#e23744' },
  { id: 'zomato_gold', name: 'Zomato Gold', color: '#e23744' },
  { id: 'eazy_diner', name: 'Eazy Diner', color: '#1a1a2e' },
];

function App() {
  const [selectedPlatform, setSelectedPlatform] = useState('swiggy_online');
  const [files, setFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobStats, setJobStats] = useState({ total: 0, completed: 0, processing: 0, failed: 0 });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [toast, setToast] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/jobs');
      setJobs(response.data.jobs || []);
      if (response.data.stats) {
        setJobStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const handleFileSelect = (event) => {
    addFiles(Array.from(event.target.files));
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    addFiles(Array.from(event.dataTransfer.files));
  };

  const addFiles = (newFiles) => {
    const xlsxFiles = newFiles.filter(f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls') || f.name.endsWith('.csv'));
    if (xlsxFiles.length !== newFiles.length) {
      showToast('Only Excel (.xlsx, .xls) or CSV files are accepted', 'error');
    }
    const filesWithPlatform = xlsxFiles.map(file => ({
      file,
      platform: selectedPlatform,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
    }));
    setFiles(prev => [...prev, ...filesWithPlatform]);
    if (xlsxFiles.length > 0) {
      showToast(`Added ${xlsxFiles.length} file(s)`, 'success');
    }
  };

  const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const handleSubmit = async () => {
    if (files.length === 0) {
      showToast('Please add at least one file', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadedKeys = [];
      const totalFiles = files.length;

      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];
        setUploadProgress(Math.round((i / totalFiles) * 80));

        const isCsv = fileData.file.name.toLowerCase().endsWith('.csv');
        const contentType = isCsv ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        const uploadUrlResponse = await api.post('/api/v1/upload', {
          filename: fileData.file.name,
          platform: fileData.platform,
          content_type: contentType,
        });

        const { upload_url, file_key } = uploadUrlResponse.data;

        const uploadResponse = await fetch(upload_url, {
          method: 'PUT',
          body: fileData.file,
          headers: { 'Content-Type': contentType },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${fileData.file.name}`);
        }

        uploadedKeys.push(file_key);
      }

      setUploadProgress(90);

      await api.post('/api/v1/jobs', {
        file_keys: uploadedKeys,
        output_filename: `Partners_Commission_Report_${new Date().toISOString().split('T')[0]}.xlsx`,
      });

      setUploadProgress(100);
      showToast('Report generation started', 'success');
      setFiles([]);
      fetchJobs();

    } catch (error) {
      console.error('Upload failed:', error);
      showToast(`Upload failed: ${error.message}`, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = (job) => {
    if (job.download_url) {
      window.open(job.download_url, '_blank');
    }
  };

  const formatDate = (dateString) => {
    // API returns UTC timestamps without timezone indicator
    // Append 'Z' to ensure it's parsed as UTC, then format in IST
    const utcDate = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDate);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <h1>Commission Report Generator</h1>
          <p>Generate commission reports from Swiggy, Zomato & Eazy Diner settlement files</p>
        </div>
        <div className="stats">
          <div className="stat"><span className="stat-num">{jobStats.total}</span> Total</div>
          <div className="stat success"><span className="stat-num">{jobStats.completed}</span> Done</div>
          <div className="stat warning"><span className="stat-num">{jobStats.processing}</span> Processing</div>
          {jobStats.failed > 0 && <div className="stat error"><span className="stat-num">{jobStats.failed}</span> Failed</div>}
        </div>
      </header>

      <div className="main">
        {/* Upload Panel */}
        <section className="panel upload-panel">
          <h2>Upload Files</h2>

          {/* Platform Pills */}
          <div className="platforms">
            {PLATFORMS.map(platform => (
              <button
                key={platform.id}
                className={`platform-pill ${selectedPlatform === platform.id ? 'active' : ''}`}
                onClick={() => setSelectedPlatform(platform.id)}
                style={{'--c': platform.color}}
              >
                {platform.name}
              </button>
            ))}
          </div>

          {/* Drop Zone */}
          <div
            className={`dropzone ${dragOver ? 'active' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
            </svg>
            <span>Drop Excel files here or click to browse</span>
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              multiple
              onChange={handleFileSelect}
              hidden
            />
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="file-list">
              {files.map(f => {
                const platform = PLATFORMS.find(p => p.id === f.platform);
                return (
                  <div key={f.id} className="file-item">
                    <div className="file-info">
                      <span className="file-name">{f.file.name}</span>
                      <span className="file-meta">
                        {formatFileSize(f.file.size)} · <span style={{color: platform?.color}}>{platform?.name}</span>
                      </span>
                    </div>
                    <button className="btn-remove" onClick={() => removeFile(f.id)}>×</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Progress Bar */}
          {uploading && (
            <div className="progress">
              <div className="progress-bar" style={{width: `${uploadProgress}%`}}></div>
            </div>
          )}

          {/* Submit Button */}
          <button
            className="btn-submit"
            onClick={handleSubmit}
            disabled={uploading || files.length === 0}
          >
            {uploading ? `Uploading... ${uploadProgress}%` : `Generate Report${files.length > 0 ? ` (${files.length})` : ''}`}
          </button>
        </section>

        {/* Jobs Panel */}
        <section className="panel jobs-panel">
          <div className="panel-header">
            <h2>Recent Jobs</h2>
            <button className="btn-refresh" onClick={fetchJobs}>Refresh</button>
          </div>

          {jobs.length === 0 ? (
            <div className="empty">
              <p>No jobs yet. Upload files to get started.</p>
            </div>
          ) : (
            <div className="job-list">
              {jobs.slice(0, 20).map(job => (
                <div key={job.job_id} className={`job-item ${job.status}`}>
                  <div className="job-status">
                    <span className={`status-badge ${job.status}`}>
                      {job.status === 'completed' && '✓'}
                      {job.status === 'failed' && '!'}
                      {(job.status === 'pending' || job.status === 'processing') && '○'}
                    </span>
                  </div>
                  <div className="job-info">
                    <span className="job-id">#{job.job_id.slice(0, 8)}</span>
                    <span className="job-date">{formatDate(job.created_at)}</span>
                  </div>
                  <div className="job-action">
                    {job.status === 'completed' && job.download_url && (
                      <button className="btn-download" onClick={() => handleDownload(job)}>
                        Download
                      </button>
                    )}
                    {job.status === 'failed' && (
                      <span className="error-text" title={job.error}>Error</span>
                    )}
                    {(job.status === 'pending' || job.status === 'processing') && (
                      <span className="processing-text">Processing...</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default App;
